'use strict';

const get = require('lodash.get');
const ItemStore = require('../stores/ItemStore');
const {trackEvent, trackSocial, trackEcommerce} = require('dibs-tracking');

const PRODUCT_CATEGORY = 'product';
const NAVIGATION_CATEGORY = 'navigation';
const DEALER_CLICK_CATEGORY = 'dealer click';
const SIMILAR_CATEGORY = 'landing module interaction';
const PROMO_CATEGORY = 'promo interaction';

const SOCIAL_TYPE_FACEBOOK = 'facebook';
const SOCIAL_TYPE_PINTEREST = 'pinterest';

// PRIVATE

function trackDescriptionInteraction(label) {
    trackEvent({
        category: PRODUCT_CATEGORY,
        action: 'pdp description interaction',
        label
    });
}

function trackStorefrontNavigation(label) {
    trackEvent({
        category: DEALER_CLICK_CATEGORY,
        action: 'click to dealer storefront',
        label
    });
}

function trackCarouselInteraction(action, image, totalImages) {
    const label = `${image}/${totalImages}`;

    trackEvent({
        category: PRODUCT_CATEGORY,
        label,
        action
    });
}

function trackSocialShare(item, type, event) {
    trackSocial({
        name: type,
        'opt_target': item.get('title'),
        event
    });
}

function addEventSuffix(event, suffix) {
    if (suffix) {
        event += ` - ${suffix}`;
    }

    return event;
}

// PUBLIC

function trackArtCreatorClick() {
    trackEvent({
        category: PRODUCT_CATEGORY,
        action: 'attribute click',
        label: 'creator'
    });
}

function trackBreadcrumbClick(actionContext, {category}) {
    trackEvent({
        category: NAVIGATION_CATEGORY,
        action: 'breadcrumb click',
        label: `pdp|${category.toLowerCase()}`
    });
}

function trackDescriptionExpand() {
    trackDescriptionInteraction('expand');
}

function trackDescriptionCollapse() {
    trackDescriptionInteraction('collapse');
}

function trackDealerViewAllClick() {
    trackStorefrontNavigation('pdp more from dealer');
}

function trackDealerLogoClick() {
    trackStorefrontNavigation('pdp more from dealer logo click');
}

function trackNextImageClick(actionContext, {image, totalImages}) {
    trackCarouselInteraction('pdp image carousel next button', image, totalImages);
}

function trackPreviousImageClick(actionContext, {image, totalImages}) {
    trackCarouselInteraction('pdp image carousel previous button', image, totalImages);
}

function trackMobileModalInteraction(actionContext, {label, image, totalImages}) {
    trackCarouselInteraction(label, image, totalImages);
}

function trackFacebookClick(actionContext, {suffix}) {
    const store = actionContext.getStore(ItemStore);

    trackSocialShare(store.getItem(), SOCIAL_TYPE_FACEBOOK, addEventSuffix('share intent', suffix));
}

function trackFacebookSuccess(actionContext, {response, suffix}) {
    if (response && response.post_id) {
        const store = actionContext.getStore(ItemStore);

        trackSocialShare(store.getItem(), SOCIAL_TYPE_FACEBOOK, addEventSuffix('share', suffix));
    }
}

function trackPinterestClick(actionContext, {suffix}) {
    const store = actionContext.getStore(ItemStore);
    const category = 'share';

    trackSocialShare(store.getItem(), SOCIAL_TYPE_PINTEREST, addEventSuffix(category, suffix));

    trackEvent({
        action: 'product from PDP',
        label: document.location.href,
        category
    });
}

function trackGridProductImpressions(actionContext, {product = {}, gridType}) {
    let price = get(product, 'price.amounts.USD') || 0;

    // Strip $ sign and remove comma
    if (price) {
        price = parseInt(price.substring(1).replace(',', ''));
    }

    trackEcommerce({
        type: 'productImpression',
        products: [{
            id: product.id,
            name: product.title,
            position: product.index,
            price: price,
            brand: product.seller.id,
            category: product.categories ? product.categories.join('/') : '',
            list: gridType
        }]
    });
}

function trackGridProductClick(actionContext, {product = {}, gridType}) {
    let price = get(product, 'price.amounts.USD') || 0;

    // Strip $ sign and remove comma
    if (price) {
        price = parseInt(price.substring(1).replace(',', ''));
    }

    trackEcommerce({
        type: 'productClick',
        actionField: {
            list: gridType
        },
        products: [{
            id: product.id,
            name: product.title,
            position: product.index,
            price: price,
            brand: product.seller.id,
            category: product.categories ? product.categories.join('/') : ''
        }]
    });
}

function trackGridPhotoDisplay() {
    trackEvent({
        category: PROMO_CATEGORY,
        action: 'related rooms carousel',
        label: 'carousel displayed'
    });
}

function trackGridPhotoClick() {
    trackEvent({
        category: PROMO_CATEGORY,
        action: 'related rooms carousel',
        label: 'room clicked'
    });
}

function trackGridPhotoGalleryOpen() {
    trackEvent({
        category: 'trade firm project image interaction',
        action: 'gallery opened',
        label: 'PDP'
    });
}

function trackSeoLinkClick(actionContext, {label}) {
    trackEvent({
        category: PRODUCT_CATEGORY,
        action: 'more ways to browse link click',
        label
    });
}

function trackDealerLinkClick(actionContext, {location}) {
    trackEvent({
        category: 'dealer click',
        action: 'click to dealer storefront',
        label: `pdp ${location}`
    });
}

function trackContactDealer(actionContext, {suffix = 'more from dealer module', action = 'clicked'}) {
    trackEvent({
        category: 'contact dealer',
        action: `contact dealer ${action}`,
        label: addEventSuffix('contact link in pdp ', suffix)
    });
}

function trackMakeOfferClick(actionContext, {isNegotiable, isPreQuote, isReturnable}) {
    const neg = isNegotiable ? 'y' : 'n';
    const ship = isPreQuote ? 'y' : 'n';
    const ret = isReturnable ? 'y' : 'n';
    trackEvent({
        category: 'make offer click',
        action: 'contact dealer form',
        label: `neg=${neg}|ship=${ship}|ret=${ret}`
    });
}

function trackAddToCart(actionContext, {trackingVpvParams}) {
    trackEcommerce({
        type: 'addToCart',
        products: [{...trackingVpvParams}]
    });
}

function trackCheckoutRedirect(actionContext, {price, offerAmount}) {
    const offer = Number.parseFloat(offerAmount, 10);
    const percentage = Math.floor(offer * 10 / price);
    const range = `${percentage}1-${percentage + 1}0`;
    trackEvent({
        category: 'make offer form interaction',
        action: 'continue offer',
        label: `neg=initial|custom|${range}%`,
        value: 1
    });
}

function trackEmailFriend(actionContext, {itemPk}) {
    trackEvent({
        category: 'email a friend',
        action: 'email a friend initiated',
        label: `item|${itemPk}`
    });
}

function trackPrint(actionContext, { withPrice }) {
    const label = `print ${withPrice ? 'with' : 'without'} price from pdp`;

    trackEvent({
        category: PRODUCT_CATEGORY,
        action: 'print item',
        label
    });
}

function trackSupportClick() {
    trackEvent({
        category: PRODUCT_CATEGORY,
        action: 'contact 1stdibs click',
        label: 'pdp'
    });
}

function trackBuyerAssuranceClick() {
    trackEvent({
        category: PRODUCT_CATEGORY,
        action: 'buyer protection',
        label: 'popup expanded'
    });
}

function trackBuyerAssuranceLinkClick() {
    trackEvent({
        category: PRODUCT_CATEGORY,
        action: 'buyer protection',
        label: 'popup link clicked'
    });
}

function trackAttributeClick(actionContext, {label}) {
    trackEvent({
        category: PRODUCT_CATEGORY,
        action: 'attribute click',
        label
    });
}

function trackShippingClick(actionContext, {label}) {
    trackEvent({
        category: PRODUCT_CATEGORY,
        action: 'view shipping and returns details',
        label
    });
}

function trackDealerInfoClick(actionContext, {url}) {
    trackEvent({
        category: PRODUCT_CATEGORY,
        action: 'view dealer details',
        label: url
    });
}

function trackExpandDealerInfo(actionContext, {itemPk}) {
    trackEvent({
        category: PRODUCT_CATEGORY,
        action: 'dealer details displayed',
        label: itemPk
    });
}

function trackSimilarItemsDisplayed() {
    trackEvent({
        category: SIMILAR_CATEGORY,
        action: 'landing module displayed',
        label: 'related items'
    });
}

function trackSimilarItemClick() {
    trackEvent({
        category: SIMILAR_CATEGORY,
        action: 'landing module clicked',
        label: 'related items'
    });
}

function trackSimilarViewAllClick() {
    trackEvent({
        category: SIMILAR_CATEGORY,
        action: 'landing module clicked',
        label: 'related items view all'
    });
}


module.exports = {
    trackArtCreatorClick,
    trackBreadcrumbClick,
    trackDescriptionExpand,
    trackDescriptionCollapse,
    trackDealerViewAllClick,
    trackDealerLogoClick,
    trackMobileModalInteraction,
    trackNextImageClick,
    trackPreviousImageClick,
    trackFacebookClick,
    trackFacebookSuccess,
    trackPinterestClick,
    trackGridProductImpressions,
    trackGridProductClick,
    trackSeoLinkClick,
    trackDealerLinkClick,
    trackEmailFriend,
    trackPrint,
    trackContactDealer,
    trackMakeOfferClick,
    trackAddToCart,
    trackCheckoutRedirect,
    trackSupportClick,
    trackBuyerAssuranceClick,
    trackBuyerAssuranceLinkClick,
    trackAttributeClick,
    trackShippingClick,
    trackExpandDealerInfo,
    trackDealerInfoClick,
    trackSimilarItemsDisplayed,
    trackSimilarItemClick,
    trackSimilarViewAllClick,
    trackGridPhotoDisplay,
    trackGridPhotoClick,
    trackGridPhotoGalleryOpen
};



// WEBPACK FOOTER //
// ./assets/js/newPdp/actions/pdpTrackingActions.es.js